import React from 'react';
import Dots from '../../../atoms/Dots/Dots';
import COMPANY_BG1 from '../../../../app/assets/images/aboutUsImages/company_bg1.webp';
import COMPANY_BG2 from '../../../../app/assets/images/aboutUsImages/company_bg2.webp';

type CompanyValue = {
  title?: string;
  number?: string;
  tone?: 'primary' | 'secondary' | 'ternary';
  description?: string;
};

type CompanySectionProps = {
  companySection?: {
    title?: string;
    intro?: string;
    description?: string;
    values?: CompanyValue[];
  };
};

const toneClasses: Record<NonNullable<CompanyValue['tone']>, string> = {
  primary: 'bg-[var(--color-primary)]',
  secondary: 'bg-[var(--color-second)]',
  ternary: 'bg-[var(--color-ternary)]',
};

const CompanySection = ({ companySection }: CompanySectionProps) => {
  if (!companySection) return null;

  const { title, intro, description, values = [] } = companySection;

  return (
    <section className="mt-8 md:mt-10 text-white bg-newbgcolor py-10 md:py-14 lg:py-16">
      <div className="max-w-[2400px] mx-auto">
      <div className="flex flex-col items-center text-center">
        <div className="text-2xl sm:text-3xl md:text-[32px] xl:text-[40px] 2xl:text-[50px] 3xl:text-[64px] font-semibold">
          {title}
        </div>
        <Dots width="w-5 md:w-7" height="h-5 md:h-7" className="mt-3 md:mt-4" />
      </div>

      <div className="mt-10 md:mt-12 mx-auto px-4 sm:px-6 py-6 md:px-8 md:py-8 lg:px-16 xl:px-24 2xl:px-32 3xl:px-48">
        <div className="block md:flex md:flex-row items-start justify-center md:gap-12 lg:gap-20 xl:gap-28">
            <div className="relative float-left mr-6 mb-3 h-[178px] w-[178px] shrink-0 sm:h-[220px] sm:w-[220px] md:float-none md:mr-0 md:mb-0 md:h-[320px] md:w-[320px] lg:h-[380px] lg:w-[380px] xl:h-[440px] xl:w-[440px] 2xl:h-[520px] 2xl:w-[520px] 3xl:h-[640px] 3xl:w-[640px]">
              <img
                src={COMPANY_BG1}
                alt={title ? `${title} primary visual` : 'Company primary visual'}
                className="w-full h-full object-cover"
              />
              <img
                src={COMPANY_BG2}
                alt={title ? `${title} secondary visual` : 'Company secondary visual'}
                className="absolute -bottom-3 -right-4 h-[46%] w-[46%] object-cover shadow-[0_12px_30px_rgba(0,0,0,0.35)] sm:-bottom-4 sm:-right-6 sm:h-[50%] sm:w-[50%] md:-bottom-5 md:-right-8 md:h-[52%] md:w-[52%] lg:-bottom-6 lg:-right-10 lg:h-[55%] lg:w-[55%]"
              />
            </div>

            <div className="relative text-left md:w-[56%] md:max-w-[620px] md:flex-1 lg:max-w-[720px] xl:max-w-[860px] 2xl:max-w-[980px] 3xl:max-w-[1150px]">
              <div className="absolute -left-5 top-[6px] hidden sm:flex flex-col items-center sm:-left-7 sm:top-[8px] md:-left-14 md:top-[12px] lg:-left-20">
                <div className="mb-1 h-[12px] w-[5px] bg-white sm:h-[14px] sm:w-[6px] md:h-[18px] md:w-[8px] lg:h-[22px] lg:w-[10px] xl:h-[26px] xl:w-[12px]" />
                <div className="h-[60px] w-[5px] bg-gradient-to-b from-[#F6B64F] to-[#E4542D] sm:h-[72px] sm:w-[6px] md:h-[84px] md:w-[8px] lg:h-[92px] lg:w-[10px] xl:h-[100px] xl:w-[12px]" />
              </div>
              <div className="pl-3 sm:pl-2 md:pl-4">
                <p className="text-white text-[10px] leading-[1.7] sm:text-[13px] md:text-[15px] lg:text-[16px] xl:text-[18px] xl:leading-[1.8] 2xl:text-[20px] 3xl:text-[26px] 3xl:leading-[1.9]">
                  {intro}
                </p>
                <p className="mt-4 text-white/70 text-[10px] leading-[1.7] sm:mt-5 sm:text-[13px] md:mt-6 md:text-[16px] lg:text-[18px] xl:text-[22px] xl:leading-[1.9] 2xl:text-[27px] 3xl:text-[34px] 3xl:leading-[2]">
                  {description}
                </p>
              </div>
            </div>
            <div className="clear-both md:hidden" />
          </div>
      </div>

      <div className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 xl:gap-14 2xl:gap-20 3xl:gap-24 text-center mx-auto px-4 md:px-8 lg:px-16 xl:px-24 2xl:px-32 3xl:px-44">
        {values.map((item) => {
          const toneClass = toneClasses[item.tone ?? 'primary'];

          return (
          <div key={item.title} className="flex justify-center">
            <div className="relative w-[290px] md:w-[360px] xl:w-[420px] 2xl:w-[500px] 3xl:w-[760px]">
              <div className="relative bg-[#0f0f0f] rounded-t-md h-[96px] md:h-28 xl:h-32 flex items-start justify-center px-5 md:px-6 xl:px-8 pt-5 md:pt-6 xl:pt-7 3xl:h-[170px] 3xl:px-12 3xl:pt-10">
                <div className="relative z-20 text-[13px] md:text-[16px] xl:text-[18px] 2xl:text-[20px] 3xl:text-[32px] font-semibold text-white">
                  {item.title}
                </div>

                <div className="absolute left-0 right-0 bottom-0 flex items-center justify-center pointer-events-none z-30 md:z-10">
                  <div className={`${toneClass} h-[4px] 3xl:h-[6px] flex-1`} />
                  <div className="w-12 md:w-16 xl:w-20 3xl:w-28" />
                  <div className={`${toneClass} h-[4px] 3xl:h-[6px] flex-1`} />
                </div>
              </div>

              <div className="absolute left-1/2 top-[74px] md:top-14 xl:top-16 transform -translate-x-1/2 md:left-0 md:right-0 md:transform-none flex items-center justify-center pointer-events-none z-30 3xl:top-[102px]">
                <div className={`mx-4 z-40`}> 
                  <div className={`w-12 md:w-16 xl:w-20 3xl:w-24 aspect-square rounded-full ${toneClass} flex items-center justify-center text-white text-[13px] md:text-[14px] xl:text-[16px] 2xl:text-[18px] 3xl:text-[26px] font-semibold border-2 border-[#0f0f0f] shadow-sm`}>{item.number}</div>
                </div>
              </div>

              <div className="relative mt-0 md:-mt-6 xl:-mt-8 mx-auto w-[270px] md:w-[250px] xl:w-[340px] 2xl:w-[390px] 3xl:w-[500px] bg-[#1f1f1f] rounded-b-md px-4 xl:px-6 pt-8 md:pt-12 xl:pt-14 pb-5 xl:pb-7 text-left md:text-center z-20 3xl:px-8 3xl:pt-16 3xl:pb-10">
                <p className="text-[12px] sm:text-[13px] md:text-[12px] xl:text-[17px] 2xl:text-[21px] 3xl:text-[28px] text-white/75 md:text-white/60 leading-[1.6] md:leading-[1.5] 3xl:leading-[1.65] text-justify md:text-center">
                  {item.description}
                </p>
              </div>
            </div>
          </div>
          );
        })}
      </div>
      </div>
    </section>
  );
};

export default CompanySection;
